import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { fmtMnt } from './PaymentGrid';

/** Сарын нэхэмжлэх (СӨХ-ийн хураамж + дулаан) — айлуудын бүртгэлтэй имэйл рүү илгээх цонх.
 *  Урьдчилан харах (айл бүрийн дүн, имэйл) → нэг айлын загварыг харах → туршилтын хаяг руу эсвэл бүгдэд илгээх. */
type Row = { unit_id: string; number: string; label: string; owner: string; emails: string[]; current: number; previous: number; total: number; lines: number };
type Preview = { period: string; kinds: string[]; heating_enabled: boolean; bank_set: boolean; rows: Row[];
  stats: { units: number; with_email: number; with_amount: number; sendable: number; total_amount: number } };

export default function InvoiceEmailModal({ heatingEnabled, defaultKind, onClose }: { heatingEnabled: boolean; defaultKind: 'soh' | 'heating'; onClose: () => void }) {
  const now = new Date();
  const [period, setPeriod] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [soh, setSoh] = useState(true);
  const [heating, setHeating] = useState(heatingEnabled && defaultKind === 'heating' ? true : heatingEnabled);
  const [prev, setPrev] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [html, setHtml] = useState<{ unit: Row; html: string } | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [confirm, setConfirm] = useState(false);
  const [log, setLog] = useState<any[]>([]);
  const kinds = useMemo(() => [...(soh ? ['soh'] : []), ...(heating ? ['heating'] : [])], [soh, heating]);

  const load = useCallback(async () => {
    if (!kinds.length) { setPrev(null); return; }
    setLoading(true); setErr(''); setResult(null);
    try {
      const p = await api.post<Preview>('/admin/fees/invoice-emails/preview', { period, kinds });
      setPrev(p);
      setSel(new Set(p.rows.filter(r => r.emails.length && r.total > 0).map(r => r.unit_id)));
      try { setLog(await api.get(`/admin/fees/invoice-emails?period=${period}`)); } catch { setLog([]); }
    } catch (e: any) { setErr(e?.message || 'Алдаа'); }
    finally { setLoading(false); }
  }, [period, kinds]);
  useEffect(() => { load(); }, [load]);

  const openHtml = async (r: Row) => {
    try {
      const h: Record<string, string> = { Authorization: `Bearer ${api.getToken() || ''}` };
      const pt = localStorage.getItem('platform_tenant_id'); if (pt) h['X-Tenant-Id'] = pt;
      const res = await fetch(`${api.baseUrl}/admin/fees/invoice-emails/html?period=${period}&unit_id=${r.unit_id}&kinds=${kinds.join(',')}`, { headers: h });
      setHtml({ unit: r, html: await res.text() });
    } catch (e: any) { setErr(e?.message || 'Загвар ачаалагдсангүй'); }
  };

  const send = async (test: boolean) => {
    if (!prev) return;
    setSending(true); setErr(''); setResult(null); setConfirm(false);
    try {
      const unit_ids = test ? [html?.unit.unit_id || Array.from(sel)[0] || prev.rows[0]?.unit_id].filter(Boolean) : Array.from(sel);
      const r = await api.post<any>('/admin/fees/invoice-emails/send', { period, kinds, unit_ids, test_email: test ? testEmail.trim() : undefined });
      setResult(r);
      try { setLog(await api.get(`/admin/fees/invoice-emails?period=${period}`)); } catch {}
    } catch (e: any) { setErr(e?.message || 'Илгээхэд алдаа гарлаа'); }
    finally { setSending(false); }
  };

  const toggle = (id: string) => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selTotal = prev ? prev.rows.filter(r => sel.has(r.unit_id)).reduce((a, r) => a + r.total, 0) : 0;
  const sentUnits = useMemo(() => new Set(log.filter(l => l.status === 'sent' && !l.is_test).map(l => l.unit_id)), [log]);

  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...card, width: 'min(980px, 96vw)', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 17 }}>✉ Нэхэмжлэх имэйлээр илгээх</h3>
          <button onClick={onClose} style={{ ...btnGhost, marginLeft: 'auto', padding: '4px 10px' }}>✕</button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 10 }}>
          <input type="month" value={period} onChange={e => setPeriod(e.target.value)} style={inp} />
          <label style={lbl}><input type="checkbox" checked={soh} onChange={e => setSoh(e.target.checked)} /> СӨХ-ийн хураамж</label>
          {heatingEnabled && <label style={lbl}><input type="checkbox" checked={heating} onChange={e => setHeating(e.target.checked)} /> 🔥 Дулаан</label>}
          {prev && <span style={{ fontSize: 12.5, color: '#5f6f66', marginLeft: 'auto' }}>
            {prev.stats.units} айл · имэйлтэй {prev.stats.with_email} · дүнтэй {prev.stats.with_amount} · илгээх боломжтой <b>{prev.stats.sendable}</b> · нийт {fmtMnt(prev.stats.total_amount)}
          </span>}
        </div>
        {prev && !prev.bank_set && <div style={note}>Тохиргоо хуудсанд СӨХ-ийн банкны данс бүртгээгүй тул нэхэмжлэхэд «Дансаар шилжүүлэх» хэсэг гарахгүй (QPay QR л гарна).</div>}
        {err && <div style={{ ...note, background: '#fdecea', color: '#b3412f' }}>{err}</div>}
        {result && <div style={{ ...note, background: result.failed ? '#fff4e5' : '#e8f3ec', color: result.failed ? '#8a4b00' : '#236844' }}>
          {result.test ? 'Туршилт: ' : ''}илгээсэн <b>{result.sent}</b>{result.failed ? `, алдаатай ${result.failed}` : ''}{result.skipped ? `, алгассан ${result.skipped} (имэйлгүй/дүнгүй)` : ''}
          {result.errors?.length > 0 && <div style={{ fontSize: 12, marginTop: 4 }}>{result.errors.join(' · ')}</div>}
        </div>}

        <div style={{ display: 'flex', gap: 12, flex: 1, minHeight: 0 }}>
          <div style={{ flex: 1, overflow: 'auto', border: '1px solid #e8e5dc', borderRadius: 10 }}>
            {loading ? <div style={{ padding: 20, color: '#5f6f66' }}>Ачаалж байна…</div> : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ background: '#f8f7f2', position: 'sticky', top: 0 }}>
                  <th style={thS}><input type="checkbox" checked={!!prev && sel.size > 0 && sel.size === prev.rows.filter(r => r.emails.length && r.total > 0).length}
                    onChange={e => setSel(e.target.checked && prev ? new Set(prev.rows.filter(r => r.emails.length && r.total > 0).map(r => r.unit_id)) : new Set())} /></th>
                  <th style={thS}>Тоот</th><th style={thS}>Эзэмшигч</th><th style={thS}>Имэйл</th>
                  <th style={{ ...thS, textAlign: 'right' }}>Энэ сар</th><th style={{ ...thS, textAlign: 'right' }}>Өмнөх</th><th style={{ ...thS, textAlign: 'right' }}>Нийт</th><th style={thS}></th>
                </tr></thead>
                <tbody>{(prev?.rows || []).map(r => {
                  const ok = r.emails.length > 0 && r.total > 0;
                  return (
                    <tr key={r.unit_id} style={{ opacity: ok ? 1 : .55, background: html?.unit.unit_id === r.unit_id ? '#eef6f1' : undefined }}>
                      <td style={tdS}><input type="checkbox" disabled={!ok} checked={sel.has(r.unit_id)} onChange={() => toggle(r.unit_id)} /></td>
                      <td style={{ ...tdS, fontWeight: 600 }}>{r.number}{sentUnits.has(r.unit_id) && <span title="Энэ сард илгээсэн" style={{ marginLeft: 4, color: '#236844' }}>✓</span>}</td>
                      <td style={tdS}>{r.owner}</td>
                      <td style={{ ...tdS, color: r.emails.length ? undefined : '#b3412f' }}>{r.emails.length ? r.emails.join(', ') : 'имэйлгүй'}</td>
                      <td style={{ ...tdS, textAlign: 'right' }}>{fmtMnt(r.current)}</td>
                      <td style={{ ...tdS, textAlign: 'right', color: r.previous ? '#8a4b00' : undefined }}>{fmtMnt(r.previous)}</td>
                      <td style={{ ...tdS, textAlign: 'right', fontWeight: 700 }}>{fmtMnt(r.total)}</td>
                      <td style={tdS}><button onClick={() => openHtml(r)} style={{ ...btnGhost, padding: '3px 8px', fontSize: 12 }}>Загвар</button></td>
                    </tr>);
                })}</tbody>
              </table>)}
          </div>
          {html && (
            <div style={{ width: '46%', minWidth: 300, display: 'flex', flexDirection: 'column', border: '1px solid #e8e5dc', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ padding: '6px 10px', background: '#f8f7f2', fontSize: 12.5, display: 'flex', alignItems: 'center' }}>
                Загвар · {html.unit.label}<button onClick={() => setHtml(null)} style={{ ...btnGhost, marginLeft: 'auto', padding: '2px 8px', fontSize: 12 }}>✕</button>
              </div>
              <iframe title="invoice" srcDoc={html.html} style={{ flex: 1, border: 'none', minHeight: 420, background: '#f3f6f3' }} sandbox="allow-same-origin" />
            </div>)}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 12 }}>
          <input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="Туршилтын имэйл" style={{ ...inp, width: 220 }} />
          <button disabled={sending || !testEmail.includes('@') || !prev} onClick={() => send(true)} style={btnGhost} title="Сонгосон/загвар харж буй нэг айлын нэхэмжлэхийг энэ хаяг руу илгээнэ">
            Туршилт илгээх
          </button>
          <span style={{ marginLeft: 'auto', fontSize: 13, color: '#5f6f66' }}>Сонгосон <b>{sel.size}</b> айл · {fmtMnt(selTotal)}</span>
          {!confirm ? (
            <button disabled={sending || sel.size === 0} onClick={() => setConfirm(true)} style={btnPrimary}>Нэхэмжлэх илгээх →</button>
          ) : (
            <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: '#b3412f' }}>{sel.size} айлын имэйл рүү илгээх үү?</span>
              <button disabled={sending} onClick={() => send(false)} style={btnPrimary}>{sending ? 'Илгээж байна…' : 'Тийм, илгээ'}</button>
              <button onClick={() => setConfirm(false)} style={btnGhost}>Болих</button>
            </span>)}
        </div>
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const card: React.CSSProperties = { background: '#fff', borderRadius: 14, padding: 18, boxShadow: '0 8px 30px rgba(0,0,0,.18)' };
const inp: React.CSSProperties = { padding: '8px 10px', border: '1.5px solid #dcdacf', borderRadius: 10, fontSize: 14, background: '#fbfaf6' };
const lbl: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5 };
const btnPrimary: React.CSSProperties = { background: 'linear-gradient(180deg,#2f7d52,#236844)', color: '#fff', border: 'none', padding: '9px 14px', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 13.5 };
const btnGhost: React.CSSProperties = { background: '#fff', color: '#236844', border: '1.5px solid #cfe0d6', padding: '8px 12px', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 13.5 };
const thS: React.CSSProperties = { textAlign: 'left', padding: '7px 8px', fontSize: 12, color: '#5f6f66', borderBottom: '1px solid #e8e5dc' };
const tdS: React.CSSProperties = { padding: '6px 8px', borderBottom: '1px solid #f0ece2', fontVariantNumeric: 'tabular-nums' };
const note: React.CSSProperties = { background: '#fff8e1', color: '#6b5300', borderRadius: 10, padding: '8px 12px', fontSize: 13, marginBottom: 10 };
